import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import { Check, X, Minus, Camera, FileText, Save, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface InspectionItem {
  checklistItemId: string;
  title: string;
  description: string;
  categoryLocationGroup: string;
  isRequired: boolean;
  status: 'pass' | 'fail' | 'na';
  notes: string;
  photos: Array<{
    url: string;
    caption: string;
    uploadedAt: string;
  }>;
}

interface Inspection {
  _id: string;
  unit: {
    _id: string;
    unitNumber: string;
  };
  checklistName: string;
  inspectionTypeName: string;
  inspector: {
    contactId?: string;
    name: string;
    email: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  scheduledDate: string;
  startTime?: string;
  endTime?: string;
  items: InspectionItem[];
  passedCount: number;
  failedCount: number;
  naCount: number;
  overallNotes: string;
}

const InspectionWalkthrough: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, inspectionId } = useParams<{ workspaceId: string; inspectionId: string }>();
  const projectId = (location.state as any)?.projectId;
  const [loading, setLoading] = useState(true);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [itemNotes, setItemNotes] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);

  // Group items by category
  const groupedItems = useMemo(() => {
    if (!inspection) return { categories: [], groups: {} };

    const groups: { [key: string]: InspectionItem[] } = {};
    const categories: string[] = [];

    inspection.items.forEach((item) => {
      const category = item.categoryLocationGroup || '';
      if (!groups[category]) {
        groups[category] = [];
        categories.push(category);
      }
      groups[category].push(item);
    });

    return { categories, groups };
  }, [inspection]);

  useEffect(() => {
    if (workspaceId && inspectionId) {
      loadInspection();
    }
  }, [workspaceId, inspectionId]);

  const loadInspection = async () => {
    if (!workspaceId || !inspectionId || !projectId) {
      toast.error('Missing required parameters');
      navigate(`/workspaces/${workspaceId}/service/inspection`);
      return;
    }

    try {
      const response = await api.get(`/workspaces/${workspaceId}/${projectId}/inspections/${inspectionId}`);
      const inspectionData = response.data.inspection;

      setInspection(inspectionData);
      setOverallNotes(inspectionData.overallNotes || '');
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading inspection:', err);
      toast.error('Failed to load inspection');
      setLoading(false);
    }
  };

  const handleBackToInspections = () => {
    navigate(`/workspaces/${workspaceId}/service/inspection`);
  };

  const handleItemStatusChange = async (itemId: string, newStatus: 'pass' | 'fail' | 'na') => {
    if (!inspection || !projectId || updatingItemId) return;

    try {
      setUpdatingItemId(itemId);
      const item = inspection.items.find(i => i.checklistItemId === itemId);
      if (!item) return;

      const response = await api.put(
        `/workspaces/${workspaceId}/${projectId}/inspections/${inspectionId}/item`,
        {
          itemId,
          status: newStatus,
          notes: item.notes
        }
      );

      setInspection(response.data.inspection);
      toast.success('Item updated');
    } catch (err: any) {
      console.error('Error updating item:', err);
      toast.error('Failed to update item');
    } finally {
      setUpdatingItemId(null);
    }
  };

  const handleSaveItemNotes = async () => {
    if (!inspection || !selectedItemId || !projectId) return;

    setSaving(true);
    try {
      const item = inspection.items.find(i => i.checklistItemId === selectedItemId);
      if (!item) return;

      const response = await api.put(
        `/workspaces/${workspaceId}/${projectId}/inspections/${inspectionId}/item`,
        {
          itemId: selectedItemId,
          status: item.status,
          notes: itemNotes
        }
      );

      setInspection(response.data.inspection);
      setSelectedItemId(null);
      setItemNotes('');
      toast.success('Notes saved');
    } catch (err: any) {
      console.error('Error saving notes:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteInspectionClick = () => {
    if (!inspection) return;

    // Check if all required items are marked
    const uncompletedRequired = inspection.items.filter(
      item => item.isRequired && item.status === 'na'
    );

    if (uncompletedRequired.length > 0) {
      toast.error(`Please complete all required items (${uncompletedRequired.length} remaining)`);
      return;
    }

    setShowCompleteConfirm(true);
  };

  const handleCompleteInspectionConfirm = async () => {
    if (!inspection || !projectId) return;

    setCompleting(true);
    setShowCompleteConfirm(false);

    try {
      await api.post(
        `/workspaces/${workspaceId}/${projectId}/inspections/${inspectionId}/complete`,
        { overallNotes }
      );

      toast.success('Inspection completed successfully');
      navigate(`/workspaces/${workspaceId}/service/inspection`);
    } catch (err: any) {
      console.error('Error completing inspection:', err);
      toast.error(err.response?.data?.message || 'Failed to complete inspection');
    } finally {
      setCompleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return '#10B981';
      case 'fail':
        return '#EF4444';
      case 'na':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pass':
        return 'Pass';
      case 'fail':
        return 'Fail';
      case 'na':
        return 'N/A';
      default:
        return 'N/A';
    }
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBackToInspections} backLabel="Inspections">
        <div className="builders-container">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: '16px'
          }}>
            <div className="builders-loading-spinner" />
            <p style={{ fontSize: '16px', color: '#6B7280', fontWeight: '500' }}>
              Loading inspection details...
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!inspection) {
    return (
      <AppLayout onBack={handleBackToInspections} backLabel="Inspections">
        <div className="builders-empty-state">
          <p>Inspection not found</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToInspections} backLabel="Inspections">
      <>
        <div className="builders-container">
          {/* Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={handleBackToInspections}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  backgroundColor: '#FFFFFF',
                  color: '#48182F',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  padding: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#48182F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
                title="Back to Inspections"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="builders-section-title">
                  Inspection: Unit {inspection.unit.unitNumber}
                </h1>
                <p className="builders-section-subtitle">
                  {inspection.checklistName} - {inspection.inspectionTypeName}
                </p>
              </div>
            </div>
          </div>

          {/* Time Information */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#48182F', marginBottom: '16px' }}>
              Timeline
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                  Scheduled Date
                </div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>
                  {new Date(inspection.scheduledDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {inspection.startTime && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                    Started At
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#10B981' }}>
                    {new Date(inspection.startTime).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              )}

              {inspection.endTime && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                    Completed At
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#48182F' }}>
                    {new Date(inspection.endTime).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              )}

              {inspection.startTime && inspection.endTime && (
                <div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                    Duration
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>
                    {(() => {
                      const duration = new Date(inspection.endTime).getTime() - new Date(inspection.startTime).getTime();
                      const minutes = Math.floor(duration / 60000);
                      const hours = Math.floor(minutes / 60);
                      const remainingMinutes = minutes % 60;
                      if (hours > 0) {
                        return `${hours}h ${remainingMinutes}m`;
                      }
                      return `${minutes}m`;
                    })()}
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                  Inspector
                </div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: '#111827' }}>
                  {inspection.inspector.name}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                  Status
                </div>
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: (() => {
                      switch (inspection.status) {
                        case 'scheduled': return '#DBEAFE';
                        case 'in-progress': return '#FEF3C7';
                        case 'completed': return '#D1FAE5';
                        case 'cancelled': return '#F3F4F6';
                        default: return '#F3F4F6';
                      }
                    })(),
                    color: (() => {
                      switch (inspection.status) {
                        case 'scheduled': return '#48182F';
                        case 'in-progress': return '#F59E0B';
                        case 'completed': return '#10B981';
                        case 'cancelled': return '#6B7280';
                        default: return '#6B7280';
                      }
                    })()
                  }}>
                    {inspection.status.charAt(0).toUpperCase() + inspection.status.slice(1).replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              backgroundColor: '#F5F3F0',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #D4CBC4',
              boxShadow: '0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                Total Items
              </div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#48182F' }}>
                {inspection.items.length}
              </div>
            </div>

            <div style={{
              backgroundColor: '#F5F3F0',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #D4CBC4',
              boxShadow: '0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                Passed
              </div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#10B981' }}>
                {inspection.passedCount}
              </div>
            </div>

            <div style={{
              backgroundColor: '#F5F3F0',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #D4CBC4',
              boxShadow: '0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                Failed
              </div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#EF4444' }}>
                {inspection.failedCount}
              </div>
            </div>

            <div style={{
              backgroundColor: '#F5F3F0',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #D4CBC4',
              boxShadow: '0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                Not Applicable
              </div>
              <div style={{ fontSize: '28px', fontWeight: '600', color: '#6B7280' }}>
                {inspection.naCount}
              </div>
            </div>
          </div>

          {/* Checklist Items */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #D4CBC4',
              backgroundColor: '#A89088'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
                Checklist Items
              </h2>
            </div>

            <div style={{ padding: '16px' }}>
              {groupedItems.categories.map((category, categoryIndex) => (
                <div key={`category-${categoryIndex}`} style={{ marginBottom: categoryIndex < groupedItems.categories.length - 1 ? '24px' : 0 }}>
                  {/* Category Header */}
                  {category && (
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#48182F',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #E5E7EB'
                    }}>
                      {category}
                    </div>
                  )}

                  {/* Category Items */}
                  {groupedItems.groups[category].map((item, index) => (
                <div
                  key={item.checklistItemId}
                  style={{
                    padding: '16px',
                    marginBottom: index < groupedItems.groups[category].length - 1 ? '12px' : 0,
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#FAFAFA'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#111827', margin: 0 }}>
                          {item.title}
                        </h3>
                        {item.isRequired && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '500',
                            color: '#EF4444',
                            backgroundColor: '#FEF2F2',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            Required
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>
                          {item.description}
                        </p>
                      )}
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginLeft: '16px'
                    }}>
                      <button
                        onClick={() => handleItemStatusChange(item.checklistItemId, 'pass')}
                        disabled={inspection.status === 'completed' || updatingItemId === item.checklistItemId}
                        style={{
                          padding: '8px',
                          border: item.status === 'pass' ? '2px solid #10B981' : '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: item.status === 'pass' ? '#D1FAE5' : '#FFFFFF',
                          cursor: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 'not-allowed' : 'pointer',
                          opacity: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 0.6 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        title="Pass"
                      >
                        <Check size={18} color={item.status === 'pass' ? '#10B981' : '#6B7280'} />
                      </button>

                      <button
                        onClick={() => handleItemStatusChange(item.checklistItemId, 'fail')}
                        disabled={inspection.status === 'completed' || updatingItemId === item.checklistItemId}
                        style={{
                          padding: '8px',
                          border: item.status === 'fail' ? '2px solid #EF4444' : '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: item.status === 'fail' ? '#FEE2E2' : '#FFFFFF',
                          cursor: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 'not-allowed' : 'pointer',
                          opacity: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 0.6 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        title="Fail"
                      >
                        <X size={18} color={item.status === 'fail' ? '#EF4444' : '#6B7280'} />
                      </button>

                      <button
                        onClick={() => handleItemStatusChange(item.checklistItemId, 'na')}
                        disabled={inspection.status === 'completed' || updatingItemId === item.checklistItemId}
                        style={{
                          padding: '8px',
                          border: item.status === 'na' ? '2px solid #6B7280' : '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: item.status === 'na' ? '#F3F4F6' : '#FFFFFF',
                          cursor: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 'not-allowed' : 'pointer',
                          opacity: (inspection.status === 'completed' || updatingItemId === item.checklistItemId) ? 0.6 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        title="Not Applicable"
                      >
                        <Minus size={18} color={item.status === 'na' ? '#6B7280' : '#9CA3AF'} />
                      </button>
                    </div>
                  </div>

                  {/* Item Notes */}
                  {item.notes && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '6px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '6px' }}>
                        Notes:
                      </div>
                      <div style={{ fontSize: '13px', color: '#111827' }}>
                        {item.notes}
                      </div>
                    </div>
                  )}

                  {/* Add/Edit Notes Button */}
                  {inspection.status !== 'completed' && (
                    <button
                      onClick={() => {
                        setSelectedItemId(item.checklistItemId);
                        setItemNotes(item.notes);
                      }}
                      className="btn btn-secondary btn-sm"
                      style={{ marginTop: '12px' }}
                    >
                      <FileText size={14} />
                      {item.notes ? 'Edit Notes' : 'Add Notes'}
                    </button>
                  )}
                </div>
              ))}
                </div>
              ))}
            </div>
          </div>

          {/* Overall Notes */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #D4CBC4',
              backgroundColor: '#A89088'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
                Overall Notes
              </h2>
            </div>
            <div style={{ padding: '24px' }}>
              <textarea
                className="form-input"
                value={overallNotes}
                onChange={(e) => setOverallNotes(e.target.value)}
                placeholder="Add overall inspection notes..."
                rows={4}
                disabled={inspection.status === 'completed'}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          {inspection.status !== 'completed' && (
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingBottom: '24px'
            }}>
              <button
                className="btn btn-success btn-lg"
                onClick={handleCompleteInspectionClick}
                disabled={completing}
              >
                <CheckCircle size={20} />
                {completing ? 'Completing...' : 'Complete Inspection'}
              </button>
            </div>
          )}

          {inspection.status === 'completed' && (
            <div style={{
              padding: '16px',
              backgroundColor: '#D1FAE5',
              border: '1px solid #10B981',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={20} color="#10B981" />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#047857' }}>
                  This inspection has been completed
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes Modal */}
        {selectedItemId && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                padding: '24px',
                borderBottom: '1px solid #E5E7EB'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  Add/Edit Notes
                </h2>
              </div>

              <div style={{ padding: '24px' }}>
                <textarea
                  className="form-input"
                  value={itemNotes}
                  onChange={(e) => setItemNotes(e.target.value)}
                  placeholder="Enter notes for this item..."
                  rows={6}
                  style={{ resize: 'vertical' }}
                  autoFocus
                />
              </div>

              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setSelectedItemId(null);
                    setItemNotes('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-md"
                  onClick={handleSaveItemNotes}
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Inspection Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showCompleteConfirm}
          title="Complete Inspection"
          message="Are you sure you want to complete this inspection? This action will finalize the inspection and mark it as completed."
          confirmText="Complete"
          cancelText="Cancel"
          confirmButtonStyle="primary"
          onConfirm={handleCompleteInspectionConfirm}
          onClose={() => setShowCompleteConfirm(false)}
        />
      </>
    </AppLayout>
  );
};

export default InspectionWalkthrough;
